export type MakeupKind = "blush" | "lipstick";
export type FacePoint = {x:number;y:number;z?:number};
export type MakeupShade = {id:string;name:string;hex:string;family:string;note:string};
export const makeupShades: Record<MakeupKind,MakeupShade[]> = {
  lipstick: [
    {id:"rosewood",name:"Rosewood",hex:"#985760",family:"Everyday",note:"A balanced rose with a little brown."},
    {id:"soft-rose",name:"Soft rose",hex:"#B77683",family:"Everyday",note:"A soft pink with a cooler edge."},
    {id:"caramel",name:"Caramel nude",hex:"#A56C53",family:"Everyday",note:"An earthy, warm nude."},
    {id:"mauve",name:"Mauve",hex:"#95607F",family:"Everyday",note:"A muted rose with violet undertones."},
    {id:"cocoa",name:"Cocoa rose",hex:"#79504D",family:"Everyday",note:"A deeper brown-led rose."},
    {id:"coral",name:"Soft coral",hex:"#C76758",family:"Reds",note:"Peachy warmth with a brighter finish."},
    {id:"cherry",name:"Cherry red",hex:"#B22545",family:"Reds",note:"A vivid, cool red."},
    {id:"brick",name:"Brick red",hex:"#A34232",family:"Reds",note:"A warmer, earthy red."},
    {id:"classic",name:"Classic red",hex:"#B12E3A",family:"Reds",note:"A balanced red to compare with warm and cool shades."},
    {id:"berry",name:"Crushed berry",hex:"#893750",family:"Deep",note:"A rich berry without bright orange warmth."},
    {id:"plum",name:"Plum",hex:"#703B60",family:"Deep",note:"A violet-led statement shade."},
    {id:"wine",name:"Wine",hex:"#672C3D",family:"Deep",note:"Deep red with a cool, muted finish."},
  ],
  blush: [
    {id:"rose",name:"Petal rose",hex:"#C57486",family:"Rose",note:"A balanced, softly rosy flush."},
    {id:"pink",name:"Cool pink",hex:"#CE789E",family:"Rose",note:"A clearer pink with a cool edge."},
    {id:"dusty",name:"Dusty rose",hex:"#A76980",family:"Rose",note:"A muted rose for a softer contrast."},
    {id:"mauve",name:"Soft mauve",hex:"#995F82",family:"Rose",note:"A purple-led rose to compare against peach."},
    {id:"peach",name:"Peach",hex:"#DB8D72",family:"Warm",note:"A soft, warm peach."},
    {id:"apricot",name:"Apricot",hex:"#D78059",family:"Warm",note:"A golden peach with more warmth."},
    {id:"coral",name:"Coral",hex:"#D57170",family:"Warm",note:"A brighter pink-and-peach flush."},
    {id:"terracotta",name:"Terracotta",hex:"#AD604E",family:"Warm",note:"Earthy warmth with more depth."},
    {id:"berry",name:"Berry",hex:"#983C64",family:"Rich",note:"Rich color that can be built up or sheered out."},
    {id:"raspberry",name:"Raspberry",hex:"#B03E68",family:"Rich",note:"A clear, stronger pink-red."},
    {id:"plum",name:"Plum",hex:"#79455F",family:"Rich",note:"A deeper violet-rose direction."},
    {id:"cinnamon",name:"Cinnamon rose",hex:"#98584E",family:"Rich",note:"Brown-based warmth with a rosy edge."},
  ],
};
const outerLip=[61,185,40,39,37,0,267,269,270,409,291,375,321,405,314,17,84,181,91,146];
const innerLip=[78,191,80,81,82,13,312,311,310,415,308,324,318,402,317,14,87,178,88,95];
export function makeMakeupMask(width:number,height:number,points:FacePoint[],kind:MakeupKind,placement=0) {
  if(points.length<468||points.some(p=>!Number.isFinite(p.x)||!Number.isFinite(p.y)))throw Error("We couldn't map this face clearly. Try a front-facing photo.");
  const canvas=document.createElement("canvas");canvas.width=width;canvas.height=height;
  const ctx=canvas.getContext("2d",{willReadFrequently:true})!;
  const p=(i:number)=>({x:points[i].x*width,y:points[i].y*height});
  const faceWidth=Math.hypot(p(234).x-p(454).x,p(234).y-p(454).y);
  if(kind==="lipstick") {
    const path=(ids:number[])=>{const coords=ids.map(p);ctx.moveTo(coords[0].x,coords[0].y);for(const c of coords.slice(1))ctx.lineTo(c.x,c.y);ctx.closePath();};
    ctx.beginPath();path(outerLip);path(innerLip);ctx.fillStyle="white";ctx.fill("evenodd");
    // Feather inward: preserve the unpainted mouth/teeth and surrounding skin.
    const soft=document.createElement("canvas");soft.width=width;soft.height=height;
    const s=soft.getContext("2d")!;s.filter=`blur(${Math.max(.6,faceWidth*.004)}px)`;s.drawImage(canvas,0,0);
    s.filter="none";s.globalCompositeOperation="destination-in";s.drawImage(canvas,0,0);
    return s.getImageData(0,0,width,height).data;
  }
  // Cheeks follow each face's eye/nose geometry, including head roll.
  for(const [eye,nose,edge] of [[133,98,234],[362,327,454]]) {
    const a=p(eye),b=p(nose),c=p(edge),e=p(eye===133?33:263);
    const cx=c.x*.45+b.x*.35+e.x*.20,cy=a.y*.25+b.y*.75 + placement*faceWidth*.06;
    const angle=Math.atan2(p(263).y-p(33).y,p(263).x-p(33).x);
    ctx.save();ctx.translate(cx,cy);ctx.rotate(angle);ctx.scale(faceWidth*.145,faceWidth*.095);
    const gradient=ctx.createRadialGradient(0,0,0,0,0,1);gradient.addColorStop(0,"rgba(255,255,255,.9)");gradient.addColorStop(.4,"rgba(255,255,255,.65)");gradient.addColorStop(1,"rgba(255,255,255,0)");
    ctx.fillStyle=gradient;ctx.fillRect(-1,-1,2,2);ctx.restore();
  }
  return ctx.getImageData(0,0,width,height).data;
}
export function tintMakeup(original:Uint8ClampedArray,mask:Uint8ClampedArray,hex:string,kind:MakeupKind,strength:number) {
  if(original.length!==mask.length||!/^#[0-9a-f]{6}$/i.test(hex))throw Error("Invalid preview inputs");
  const rgb=[1,3,5].map(i=>parseInt(hex.slice(i,i+2),16));
  const out=new Uint8ClampedArray(original);let weighted=0,total=0;
  for(let i=0;i<mask.length;i+=4){const m=mask[i+3]/255;weighted+=(original[i]*.299+original[i+1]*.587+original[i+2]*.114)*m;total+=m;}
  const reference=weighted/Math.max(total,1);
  for(let i=0;i<mask.length;i+=4){
    const alpha=mask[i+3]/255*Math.max(0,Math.min(1,strength))*(kind==="blush"?.6:.94);if(!alpha)continue;
    const lum=original[i]*.299+original[i+1]*.587+original[i+2]*.114;
    const shade=Math.max(.35,Math.min(1.8,lum/Math.max(24,reference)));
    for(let c=0;c<3;c++)out[i+c]=original[i+c]*(1-alpha)+Math.min(255,rgb[c]*shade)*alpha;
  }
  return out;
}
